import { Mark, mergeAttributes } from '@tiptap/core';

/**
 * Converts RGB color string to hex
 * @param {string} rgb - e.g. "rgb(230, 0, 0)"
 * @returns {string} - e.g. "#e60000"
 */
function rgbToHex(rgb) {
    const match = rgb.match(/rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/);
    if (!match) {return null;}

    const r = parseInt(match[1], 10);
    const g = parseInt(match[2], 10);
    const b = parseInt(match[3], 10);

    return '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('');
}

/**
 * Checks if background-color is transparent or effectively invisible
 * @param {string} style - style attribute value
 * @returns {boolean}
 */
function isTransparentBackground(style) {
    if (!style) {return true;}
    
    const bgMatch = style.match(/background-color:\s*([^;]+)/);
    if (!bgMatch) {return true;}
    
    const value = bgMatch[1].trim().toLowerCase();
    return value === 'transparent' || 
           value === 'initial' || 
           value === 'inherit' ||
           value === 'rgba(0, 0, 0, 0)' ||
           value === 'rgba(0,0,0,0)';
}

/**
 * TipTap v2 extension that converts legacy background-color spans to highlight marks
 * 
 * Parses: <span style="background-color: rgb(230, 0, 0);">text</span>
 * Outputs: <mark data-color="#e60000" style="background-color: rgb(230, 0, 0); color: inherit;">text</mark>
 * 
 * Ignores: <span style="background-color: transparent;">text</span>
 */
export const LegacyHighlight = Mark.create({
    name: 'highlight',

    // Higher priority than TextStyle (default 100) to ensure we parse background-color spans first
    priority: 1001,

    addOptions() {
        return {
            multicolor: true,
            HTMLAttributes: {}
        };
    },

    addAttributes() {
        return {
            color: {
                default: null,
                parseHTML: element => {
                    // First check data-color attribute (TipTap format)
                    const dataColor = element.getAttribute('data-color');
                    if (dataColor) {
                        return dataColor;
                    }

                    // Fallback to parsing background-color from style (legacy format)
                    const style = element.getAttribute('style');
                    if (style) {
                        const bgMatch = style.match(/background-color:\s*(rgb\([^)]+\))/);
                        if (bgMatch) {
                            return rgbToHex(bgMatch[1]);
                        }
                    }
                    return null;
                },
                renderHTML: attributes => {
                    if (!attributes.color) {
                        return {};
                    }

                    // Convert hex to RGB for style
                    const hex = attributes.color.replace('#', '');
                    const r = parseInt(hex.substring(0, 2), 16);
                    const g = parseInt(hex.substring(2, 4), 16);
                    const b = parseInt(hex.substring(4, 6), 16);

                    return {
                        'data-color': attributes.color,
                        'style': `background-color: rgb(${r}, ${g}, ${b}); color: inherit;`
                    };
                }
            }
        };
    },

    parseHTML() {
        return [
            // Parse TipTap's native mark element
            {
                tag: 'mark[data-color]',
                priority: 60
            },
            // Parse any element with background-color style (legacy Quill format)
            {
                tag: '[style*="background-color"]',
                consuming: false,
                priority: 60,
                getAttrs: element => {
                    const style = element.getAttribute('style');
                    if (isTransparentBackground(style)) {
                        return false;
                    }
                    return {};
                }
            },
            // Parse mark without data-color (basic highlight)
            {
                tag: 'mark',
                priority: 50
            }
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return ['mark', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
    },

    addCommands() {
        return {
            setHighlight: attributes => ({ commands }) => {
                return commands.setMark(this.name, attributes);
            },
            toggleHighlight: attributes => ({ commands }) => {
                return commands.toggleMark(this.name, attributes);
            },
            unsetHighlight: () => ({ commands }) => {
                return commands.unsetMark(this.name);
            }
        };
    }
});

export default LegacyHighlight;
